import { useCallback, useMemo } from 'react';

import { isOptionGroup } from '../../Select.helpers';
import { useSelectRootContext } from '../root/SelectRootContext';
import { useSelectRootFloatingContext } from '../root/SelectRootFloatingContext';

import type { SelectOptionProps } from './SelectOption';
import type { InternalSelectOption } from '../../Select.types';

const useSelectOption = () => {
    const {
        mode,
        selection: { isOptionSelected, handleSelect },
        options: { activeIndex, optionRenderer },
        hideCheckmark,
        checkmarkAlign
    } = useSelectRootContext();
    const {
        floatingInteractions: { getItemProps }
    } = useSelectRootFloatingContext();

    const getOptionProps = useCallback(
        (
            option: InternalSelectOption,
            index: number
        ): SelectOptionProps & {
            'data-active': boolean;
            'data-testid'?: string | undefined;
        } => ({
            checkmarkAlign,
            hideCheckmark,
            renderOptionContent: optionRenderer,
            selected: isOptionSelected(option),
            active: activeIndex === index,
            id: `item-${index}`,
            option,
            mode,
            'data-active': activeIndex === index,
            'aria-label': option['aria-label'] ?? undefined,
            'data-testid': option['data-testid'] ?? undefined,
            onSelect: handleSelect,
            getItemProps,
            tabIndex: activeIndex === index ? 0 : -1,
            ...(isOptionGroup(option)
                ? {
                      role: 'group',
                      'aria-disabled': true
                  }
                : {
                      'aria-selected': isOptionSelected(option),
                      'aria-disabled': option.disabled,
                      role: 'option'
                  })
        }),
        [
            checkmarkAlign,
            activeIndex,
            isOptionSelected,
            mode,
            handleSelect,
            getItemProps,
            hideCheckmark,
            optionRenderer
        ]
    );

    return useMemo(() => ({ getOptionProps }), [getOptionProps]);
};

export default useSelectOption;
